import { ChevronDown, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import type { ApartmentFilterOptions } from '@services/livability/types'

import { cn } from '@/lib/utils'
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback'
import type { ApartmentFilters } from '../../utils/apartmentFilterUrl'
import {
  applyFilterPatch,
  formatChipLabel,
  isFilterDefActive,
  type FilterDef
} from './filterDefs'

const RANGE_DEBOUNCE_MS = 350

interface FilterChipProps {
  def: FilterDef
  filters: ApartmentFilters
  filterOptions?: ApartmentFilterOptions
  onChange: (next: ApartmentFilters) => void
  onRemove: () => void
  openPopover?: boolean
  onPopoverOpened?: () => void
  isPending?: boolean
}

export function FilterChip({
  def,
  filters,
  filterOptions,
  onChange,
  onRemove,
  openPopover = false,
  onPopoverOpened,
  isPending = false
}: FilterChipProps) {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!openPopover) return
    const timer = window.setTimeout(() => {
      setIsOpen(true)
      onPopoverOpened?.()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [openPopover, onPopoverOpened])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  const isActive = isFilterDefActive(def, filters)
  const label =
    isPending && !isActive
      ? `${def.label}…`
      : formatChipLabel(def, filters, filterOptions)

  const handleRemove = () => {
    setIsOpen(false)
    onRemove()
  }

  if (def.type === 'boolean') {
    return (
      <Badge
        variant="secondary"
        className="cursor-pointer gap-1.5 rounded-full py-1.5 pl-3 pr-2 text-sm font-medium"
        onClick={handleRemove}
      >
        {label}
        <X className="h-3.5 w-3.5" />
      </Badge>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <div
        className={cn(
          'inline-flex items-stretch rounded-full border bg-white',
          isPending && !isActive
            ? 'border-dashed border-ink/25 bg-ink/[0.02]'
            : 'border-ink/[0.08]'
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium',
              'outline-none focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1',
              'hover:bg-ink/[0.03]',
              isPending && !isActive ? 'text-muted-foreground' : 'text-ink',
              isActive || isPending ? 'rounded-l-full' : 'rounded-full'
            )}
          >
            {label}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        {(isActive || isPending) && (
          <button
            type="button"
            aria-label={`Odstrániť filter ${def.label}`}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              handleRemove()
            }}
            className={cn(
              'flex items-center rounded-r-full border-l border-ink/[0.08] px-2',
              'text-muted-foreground outline-none',
              'hover:bg-ink/[0.03] hover:text-ink',
              'focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1'
            )}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent>
        <FilterChipEditor
          def={def}
          filters={filters}
          filterOptions={filterOptions}
          onChange={onChange}
          onClose={() => handleOpenChange(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

function FilterChipEditor({
  def,
  filters,
  filterOptions,
  onChange,
  onClose
}: {
  def: FilterDef
  filters: ApartmentFilters
  filterOptions?: ApartmentFilterOptions
  onChange: (next: ApartmentFilters) => void
  onClose: () => void
}) {
  if (def.type === 'range') {
    return (
      <RangeEditor
        def={def}
        filters={filters}
        onChange={onChange}
        onClose={onClose}
      />
    )
  }

  if (def.type === 'number') {
    return (
      <NumberEditor
        def={def}
        filters={filters}
        onChange={onChange}
        onClose={onClose}
      />
    )
  }

  if (def.type === 'multiButtonGroup') {
    const selected = new Set(filters.roomCounts ?? [])
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">{def.label}</Label>
        <p className="text-xs text-muted-foreground">
          Vyberte jednu alebo viac možností
        </p>
        <div className="flex flex-wrap gap-1.5">
          {def.options.map((option) => {
            const isSelected = selected.has(option.value)
            return (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={isSelected ? 'default' : 'outline'}
                onClick={() => {
                  const next = new Set(selected)
                  if (isSelected) {
                    next.delete(option.value)
                  } else {
                    next.add(option.value)
                  }
                  const roomCounts = [...next].sort((a, b) => a - b)
                  onChange(
                    applyFilterPatch(filters, {
                      roomCounts: roomCounts.length > 0 ? roomCounts : undefined
                    })
                  )
                }}
              >
                {option.label}
              </Button>
            )
          })}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="w-full"
          onClick={onClose}
        >
          Hotovo
        </Button>
      </div>
    )
  }

  if (def.type !== 'select') return null

  const options = def.getOptions(filterOptions, filters)
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{def.label}</Label>
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {def.id === 'district' && filters.city
            ? 'Pre toto mesto nie sú mestské časti (skontroluj typ Prenájom/Predaj alebo obnov stránku).'
            : 'Žiadne možnosti na výber.'}
        </p>
      ) : (
        <Select
          value={filters[def.key] ?? undefined}
          onValueChange={(value) => {
            const patch: Partial<ApartmentFilters> =
              def.key === 'city'
                ? { city: value, district: undefined }
                : { [def.key]: value }
            onChange(applyFilterPatch(filters, patch))
            onClose()
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={`Vybrať ${def.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}

function RangeEditor({
  def,
  filters,
  onChange,
  onClose
}: {
  def: Extract<FilterDef, { type: 'range' }>
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
  onClose: () => void
}) {
  const [min, setMin] = useState(filters[def.minKey]?.toString() ?? '')
  const [max, setMax] = useState(filters[def.maxKey]?.toString() ?? '')
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const { debounced: commitDebounced, flush, cancel } = useDebouncedCallback(
    (nextMin: string, nextMax: string) => {
      const parsedMin = nextMin.trim() ? Number(nextMin) : undefined
      const parsedMax = nextMax.trim() ? Number(nextMax) : undefined
      onChange(
        applyFilterPatch(filtersRef.current, {
          [def.minKey]:
            parsedMin !== undefined && Number.isFinite(parsedMin)
              ? parsedMin
              : undefined,
          [def.maxKey]:
            parsedMax !== undefined && Number.isFinite(parsedMax)
              ? parsedMax
              : undefined
        })
      )
    },
    RANGE_DEBOUNCE_MS
  )

  useEffect(() => {
    if (
      filters[def.minKey] === undefined &&
      filters[def.maxKey] === undefined
    ) {
      cancel()
      setMin('')
      setMax('')
    }
  }, [cancel, def.maxKey, def.minKey, filters])

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {def.label}
        {def.unit ? ` (${def.unit})` : ''}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder="Min"
          value={min}
          onChange={(e) => {
            setMin(e.target.value)
            commitDebounced(e.target.value, max)
          }}
          onBlur={flush}
          autoFocus
          className="w-full"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="number"
          placeholder="Max"
          value={max}
          onChange={(e) => {
            setMax(e.target.value)
            commitDebounced(min, e.target.value)
          }}
          onBlur={flush}
          className="w-full"
        />
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="w-full"
        onClick={() => {
          flush()
          onClose()
        }}
      >
        Hotovo
      </Button>
    </div>
  )
}

function NumberEditor({
  def,
  filters,
  onChange,
  onClose
}: {
  def: Extract<FilterDef, { type: 'number' }>
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
  onClose: () => void
}) {
  const [value, setValue] = useState(filters[def.key]?.toString() ?? '')

  const { debounced: commitDebounced, flush } = useDebouncedCallback(
    (next: string) => {
      const parsed = next.trim() ? Number(next) : undefined
      onChange(
        applyFilterPatch(filters, {
          [def.key]:
            parsed !== undefined
              ? Math.min(def.max, Math.max(def.min, parsed))
              : undefined
        })
      )
    },
    RANGE_DEBOUNCE_MS
  )

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{def.label}</Label>
      <Input
        type="number"
        min={def.min}
        max={def.max}
        placeholder={def.placeholder}
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          commitDebounced(e.target.value)
        }}
        onBlur={flush}
        autoFocus
      />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="w-full"
        onClick={() => {
          flush()
          onClose()
        }}
      >
        Hotovo
      </Button>
    </div>
  )
}
